import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  User,
  signInWithRedirect,
  getRedirectResult,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
  sendEmailVerification,
  sendPasswordResetEmail,
  deleteUser,
} from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, googleProvider } from '../lib/firebase';
import { useToast } from '@/hooks/use-sonner-toast';
import { Loading } from '@/components/ui/loading';
import { createUserDocument } from '@/lib/firebase/firestore';
import { initialElements } from '@/lib/constants';

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<User>;
  createUserWithEmail: (email: string, password: string, displayName: string) => Promise<User>;
  sendVerificationEmail: () => Promise<void>;
  checkEmailVerification: () => Promise<void>; // Function to manually check email verification
  sendPasswordReset: (email: string) => Promise<boolean>; // Function to send password reset email
  isEmailVerified: boolean;
  isAuthenticated: boolean; // New property to check if user is authenticated AND verified
  signOut: () => Promise<void>;
  deleteAccount: () => Promise<void>; // Function to delete user account
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [verificationCheckInterval, setVerificationCheckInterval] = useState<NodeJS.Timeout | null>(
    null
  );
  const { toast } = useToast();

  useEffect(() => {
    // Set loading to true initially
    setLoading(true);

    // Set up auth state listener first
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Force token refresh to avoid CORS issues

          await user.getIdToken(true);

          // Create initial user document with empty goals
          // We'll let the GoalPanel component handle fetching the initial goals
          await createUserDocument(user.uid, {
            gameState: {
              elements: initialElements,
              favorites: [],
              discoveries: [],
              goals: [], // Start with empty goals - GoalPanel will fetch them
              points: 0,
              skipCount: 5,
              zoomLevel: 2, // Set default zoom level
            },
            createdAt: new Date(),
            lastLoginAt: new Date(),
            // Pass user profile data
            email: user.email,
            displayName: user.displayName,
            photoURL: user.photoURL,
          });

          // Check if email is verified
          const isVerified = user.emailVerified;
          setIsEmailVerified(isVerified);

          // User is authenticated if:
          // 1. They signed in with Google (provider.id includes 'google')
          // 2. OR their email is verified
          const authMethods = await user.getIdTokenResult();
          const signInProvider = authMethods.signInProvider;
          const isGoogleAuth = signInProvider && signInProvider.includes('google');

          // Set authenticated status
          const authenticated = isVerified || isGoogleAuth;
          setIsAuthenticated(authenticated);

          setCurrentUser(user);

          // Show welcome toast when user is logged in
          toast({
            title: 'Welcome to VibeCraft',
            description: `Signed in as ${user.displayName || 'Guest'}`,
          });

          // Check if we have a stored redirect URL
          const redirectUrl = localStorage.getItem('authRedirectUrl');
          if (redirectUrl) {
            // Clear the stored URL
            localStorage.removeItem('authRedirectUrl');

            // Only redirect if we're not already at the target URL
            if (window.location.href !== redirectUrl) {
              window.location.href = redirectUrl;
              return; // Exit early as we're redirecting
            }
          }
        } catch (error) {
          setCurrentUser(user); // Still set the user even if there was an error
        }
      } else {
        setCurrentUser(null);
      }

      setLoading(false);
    });

    // Check for redirect result after setting up the auth state listener
    // but only if we're not on the auth handler page (let that component handle it)
    if (!window.location.pathname.includes('/__/auth/handler')) {
      getRedirectResult(auth)
        .then(async (result) => {
          if (result) {
            // User successfully signed in after redirect

            try {
              // Force token refresh immediately after sign-in
              await result.user.getIdToken(true);

              // The onAuthStateChanged handler will handle the rest
            } catch (error) {
              // Try to recover by forcing a page reload
              if (window.location.pathname !== '/') {
                window.location.href = '/';
              }
            }
          } else {
          }
        })
        .catch((error) => {
          // Only log and show toast for real errors
          if (error.code !== 'auth/no-auth-event') {
            // Log additional information for debugging

            toast({
              title: 'Sign in failed',
              description: `There was a problem signing in: ${error.message}`,
              variant: 'destructive',
            });

            // Try to recover by forcing a page reload
            if (window.location.pathname !== '/') {
              window.location.href = '/';
            }
          }
        });
    }

    return unsubscribe;
  }, [toast]);

  const signInWithGoogle = async () => {
    try {
      // Don't set loading to true yet - wait until after redirect
      // setLoading(true);

      // Reset the provider to avoid any lingering configuration
      const provider = new GoogleAuthProvider();

      // Add scopes
      provider.addScope('profile');
      provider.addScope('email');

      // Set custom parameters for the Google provider
      provider.setCustomParameters({
        prompt: 'select_account',
      });

      // Store the current URL in localStorage to redirect back after auth
      localStorage.setItem('authRedirectUrl', window.location.href);

      // Show a toast to inform the user they're being redirected
      toast({
        title: 'Redirecting to sign in',
        description: "You'll be redirected to the Google sign-in page.",
      });

      // Get the current hostname for the redirect
      const currentHostname = window.location.hostname;

      // Force the auth domain to match the current hostname for preview channels
      if (currentHostname.includes('vibecraft-5c54c--')) {
        // Reconfigure auth for preview channels
        auth.config.authDomain = currentHostname;
      }

      // Use a try-catch block specifically for the redirect
      try {
        // Redirect to Google sign-in
        // This will navigate away from the current page
        await signInWithRedirect(auth, provider);

        // Code after this point won't execute until the user returns to the app
        // The redirect result will be handled in the useEffect hook
      } catch (redirectError: any) {
        throw redirectError; // Re-throw to be caught by the outer catch
      }
    } catch (error: any) {
      // More detailed error message
      let errorMessage = 'There was a problem signing in with Google.';
      if (error.code) {
        switch (error.code) {
          case 'auth/network-request-failed':
            errorMessage = 'Network error occurred. Please check your connection.';
            break;
          case 'auth/invalid-api-key':
            errorMessage = 'Invalid API key. Please contact the administrator.';
            break;
          case 'auth/unauthorized-domain':
            errorMessage = `This domain (${window.location.hostname}) is not authorized for authentication. Please contact the administrator.`;
            break;
          default:
            errorMessage = `Error: ${error.message || error.code || 'Unknown error'}`;
        }
      }

      toast({
        title: 'Sign in failed',
        description: errorMessage,
        variant: 'destructive',
      });

      // Reset loading state in case of error
      setLoading(false);
    }
  };

  const signInWithEmail = async (email: string, password: string) => {
    try {
      setLoading(true);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      toast({
        title: 'Sign in successful',
        description: 'You have been signed in to VibeCraft.',
      });

      return userCredential.user;
    } catch (error: any) {
      let errorMessage = 'There was a problem signing in.';
      if (error.code) {
        switch (error.code) {
          case 'auth/invalid-email':
            errorMessage = 'Invalid email address format.';
            break;
          case 'auth/user-disabled':
            errorMessage = 'This account has been disabled.';
            break;
          case 'auth/user-not-found':
            errorMessage = 'No account found with this email.';
            break;
          case 'auth/wrong-password':
            errorMessage = 'Incorrect password.';
            break;
          default:
            errorMessage = `Error: ${error.message || error.code || 'Unknown error'}`;
        }
      }

      toast({
        title: 'Sign in failed',
        description: errorMessage,
        variant: 'destructive',
      });

      setLoading(false);
      throw error;
    }
  };

  const createUserWithEmail = async (email: string, password: string, displayName: string) => {
    try {
      setLoading(true);

      // Create the user with email and password
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Update the user's profile with the display name

      await updateProfile(user, {
        displayName: displayName,
      });

      // Force a refresh to ensure the displayName is updated
      await user.reload();

      // Create the user document in Firestore with the display name
      await createUserDocument(user.uid, {
        gameState: {
          elements: initialElements,
          favorites: [],
          discoveries: [],
          goals: [],
          points: 0,
          skipCount: 5,
          zoomLevel: 2,
        },
        createdAt: new Date(),
        lastLoginAt: new Date(),
        email: user.email,
        displayName: displayName, // Use the provided display name
        photoURL: user.photoURL,
      });

      // Send email verification
      await sendEmailVerification(user);

      toast({
        title: 'Account created',
        description:
          'Your account has been created successfully. Please check your email to verify your account.',
      });

      return user;
    } catch (error: any) {
      let errorMessage = 'There was a problem creating your account.';
      if (error.code) {
        switch (error.code) {
          case 'auth/email-already-in-use':
            errorMessage = 'An account with this email already exists.';
            break;
          case 'auth/invalid-email':
            errorMessage = 'Invalid email address format.';
            break;
          case 'auth/weak-password':
            errorMessage = 'Password is too weak. Please use a stronger password.';
            break;
          default:
            errorMessage = `Error: ${error.message || error.code || 'Unknown error'}`;
        }
      }

      toast({
        title: 'Sign up failed',
        description: errorMessage,
        variant: 'destructive',
      });

      setLoading(false);
      throw error;
    }
  };

  // Function to check email verification status and refresh token
  const checkEmailVerification = useCallback(async () => {
    if (!currentUser) return;

    // Only check for users who aren't verified yet
    if (!currentUser.emailVerified) {
      try {
        // Reload the user to get the latest verification status
        await currentUser.reload();

        // Get the updated user object
        const refreshedUser = auth.currentUser;

        if (refreshedUser && refreshedUser.emailVerified) {
          // Update the local user state with the refreshed user
          setCurrentUser(refreshedUser);
          setIsEmailVerified(true);

          // Set authenticated status
          setIsAuthenticated(true); // Email is now verified

          // Show success toast
          toast({
            title: 'Email verified!',
            description: 'Your email has been successfully verified. You can now access the Forge.',
            variant: 'success',
          });

          // Clear the interval since verification is complete
          if (verificationCheckInterval) {
            clearInterval(verificationCheckInterval);
            setVerificationCheckInterval(null);
          }
        }
      } catch (error) {}
    }
  }, [currentUser, toast, verificationCheckInterval]);

  // Start checking for email verification when user is logged in but not verified
  useEffect(() => {
    // Only set up interval if user exists, is not verified, and interval isn't already running
    if (currentUser && !isEmailVerified && !verificationCheckInterval) {
      // Check immediately first
      checkEmailVerification();

      // Then set up interval to check every 5 seconds
      const interval = setInterval(checkEmailVerification, 5000);
      setVerificationCheckInterval(interval);
    }

    // Clean up interval when component unmounts or user becomes verified
    return () => {
      if (verificationCheckInterval) {
        clearInterval(verificationCheckInterval);
      }
    };
  }, [currentUser, isEmailVerified, verificationCheckInterval, checkEmailVerification]);

  const sendVerificationEmail = async () => {
    try {
      if (!currentUser) {
        throw new Error('No user is currently signed in');
      }

      await sendEmailVerification(currentUser);

      toast({
        title: 'Verification email sent',
        description: 'Please check your email to verify your account.',
      });

      // Start checking for verification after sending the email
      if (!verificationCheckInterval) {
        checkEmailVerification();
        const interval = setInterval(checkEmailVerification, 5000);
        setVerificationCheckInterval(interval);
      }
    } catch (error: any) {
      toast({
        title: 'Failed to send verification email',
        description: error.message || 'An error occurred while sending the verification email.',
        variant: 'destructive',
      });

      throw error;
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      toast({
        title: 'Signed out',
        description: 'You have been signed out of VibeCraft.',
      });
    } catch (error) {
      toast({
        title: 'Sign out failed',
        description: 'There was a problem signing out.',
        variant: 'destructive',
      });
    }
  };

  // Function to delete user account
  const deleteAccount = async () => {
    try {
      if (!currentUser) {
        throw new Error('No user is currently signed in');
      }

      // Delete the user from Firebase Authentication
      await deleteUser(currentUser);

      toast({
        title: 'Account Deleted',
        description: 'Your account has been successfully deleted.',
      });

      // Redirect to landing page
      window.location.href = '/';
    } catch (error: any) {
      console.error('Error deleting account:', error);

      let errorMessage = 'There was a problem deleting your account.';
      if (error.code) {
        switch (error.code) {
          case 'auth/requires-recent-login':
            errorMessage =
              'For security reasons, please sign out and sign in again before deleting your account.';
            break;
          default:
            errorMessage = `Error: ${error.message || error.code || 'Unknown error'}`;
        }
      }

      toast({
        title: 'Account Deletion Failed',
        description: errorMessage,
        variant: 'destructive',
      });

      throw error;
    }
  };

  // Function to send password reset email
  const sendPasswordReset = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);

      toast({
        title: 'Password reset email sent',
        description: 'Please check your email for instructions to reset your password.',
      });

      return true;
    } catch (error: any) {
      let errorMessage = 'There was a problem sending the password reset email.';
      if (error.code) {
        switch (error.code) {
          case 'auth/invalid-email':
            errorMessage = 'Invalid email address format.';
            break;
          case 'auth/user-not-found':
            errorMessage = 'No account found with this email.';
            break;
          default:
            errorMessage = `Error: ${error.message || error.code || 'Unknown error'}`;
        }
      }

      toast({
        title: 'Password reset failed',
        description: errorMessage,
        variant: 'destructive',
      });

      throw error;
    }
  };

  const value = {
    currentUser,
    loading,
    signInWithGoogle,
    signInWithEmail,
    createUserWithEmail,
    sendVerificationEmail,
    checkEmailVerification,
    sendPasswordReset,
    isEmailVerified,
    isAuthenticated,
    signOut,
    deleteAccount,
  };

  // Always render the provider, even when loading
  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        <div className="flex items-center justify-center min-h-screen">
          <Loading size={40} />
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};
